/**
 * Video Progress Service
 * Persists video watch progress per user in localStorage.
 * Key format: "videoProgress:v1:{userId}"
 */

/**
 * @typedef {Object} VideoProgress
 * @property {number} viewCount - Number of times this video has been watched past threshold
 * @property {number} secondsWatched - Total seconds watched (forward playback only)
 * @property {string|null} lastWatchedAt - ISO timestamp of last update
 * @property {boolean} watched - True if video has been watched past threshold
 */

const STORAGE_PREFIX = 'videoProgress:v1:';
export const DEFAULT_THRESHOLD_SECONDS = 10;

function getStorageKey(userId) {
  return `${STORAGE_PREFIX}${userId}`;
}

/**
 * Validate and sanitize a video progress entry.
 * @param {*} entry - Raw entry from localStorage
 * @returns {VideoProgress|null}
 */
function validateEntry(entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return null;
  }
  return {
    viewCount: typeof entry.viewCount === 'number' ? entry.viewCount : 0,
    secondsWatched: typeof entry.secondsWatched === 'number' ? entry.secondsWatched : 0,
    lastWatchedAt: typeof entry.lastWatchedAt === 'string' ? entry.lastWatchedAt : null,
    watched: Boolean(entry.watched),
  };
}

/**
 * Read all progress for a user, with corruption recovery.
 * @param {string} userId
 * @returns {Object.<string, VideoProgress>}
 */
function readAll(userId) {
  if (!userId) return {};
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return {};
    
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    
    // Validate each entry, removing corrupted ones
    const sanitized = {};
    for (const [videoId, entry] of Object.entries(parsed)) {
      const validated = validateEntry(entry);
      if (validated) {
        sanitized[videoId] = validated;
      }
    }
    return sanitized;
  } catch {
    // Corrupted JSON or quota exceeded on read — return empty
    return {};
  }
}

function writeAll(userId, data) {
  if (!userId) return;
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/**
 * Get progress for a single video.
 * @param {string} userId - User identifier
 * @param {string|number} videoId - Video identifier (can be string, number, but not null/undefined/empty)
 * @returns {VideoProgress|null}
 */
export function getVideoProgress(userId, videoId) {
  if (!userId || videoId == null || videoId === '') return null;
  const all = readAll(userId);
  return all[String(videoId)] || null;
}

/**
 * Get progress for all videos.
 * @param {string} userId - User identifier
 * @returns {Object.<string, VideoProgress>}
 */
export function getAllProgress(userId) {
  return readAll(userId);
}

/**
 * Update seconds watched for a video (called from throttled flush).
 * Marks as watched once threshold is met.
 * Increments viewCount once per session (caller controls sessionCounted flag).
 * @param {string} userId - User identifier
 * @param {string|number} videoId - Video identifier
 * @param {Object} options
 * @param {number} options.secondsWatched - Total seconds watched so far
 * @param {number} [options.videoDuration] - Video duration in seconds (optional)
 * @param {boolean} [options.countNewView] - Whether to increment view count
 * @returns {VideoProgress|null}
 */
export function updateVideoProgress(userId, videoId, { secondsWatched, videoDuration, countNewView }) {
  if (!userId || videoId == null || videoId === '') return null;

  const all = readAll(userId);
  const key = String(videoId);
  const existing = all[key] || {
    viewCount: 0,
    secondsWatched: 0,
    lastWatchedAt: null,
    watched: false,
  };

  const threshold = videoDuration
    ? Math.min(DEFAULT_THRESHOLD_SECONDS, videoDuration * 0.8)
    : DEFAULT_THRESHOLD_SECONDS;

  const totalSeconds = Math.max(existing.secondsWatched, secondsWatched);
  const isWatched = totalSeconds >= threshold;

  const updated = {
    ...existing,
    secondsWatched: totalSeconds,
    lastWatchedAt: new Date().toISOString(),
    watched: existing.watched || isWatched,
    viewCount: countNewView ? existing.viewCount + 1 : existing.viewCount,
  };

  all[key] = updated;
  writeAll(userId, all);
  return updated;
}

/**
 * Get progress summary grouped by position.
 * @param {string} userId - User identifier
 * @param {Array<Object>} microconceptos - Full list of microconceptos with id, posicion
 * @returns {{ total: number, watched: number, byPosition: Object.<string, { total: number, watched: number }> }}
 */
export function getProgressByPosition(userId, microconceptos) {
  const progress = readAll(userId);
  const byPosition = {};
  let totalWatched = 0;

  for (const mc of microconceptos) {
    const pos = mc.posicion || 'Sin posición';
    if (!byPosition[pos]) {
      byPosition[pos] = { total: 0, watched: 0 };
    }
    byPosition[pos].total++;

    const vid = mc.id;
    if (vid != null && vid !== '') {
      const key = String(vid);
      if (progress[key]?.watched) {
        byPosition[pos].watched++;
        totalWatched++;
      }
    }
  }

  return {
    total: microconceptos.length,
    watched: totalWatched,
    byPosition,
  };
}

/**
 * Get count of watched videos for a user.
 * @param {string} userId - User identifier
 * @returns {number}
 */
export function getWatchedCount(userId) {
  const all = readAll(userId);
  return Object.values(all).filter(entry => entry.watched).length;
}

/**
 * Reset progress for a single video (mark as unwatched).
 * @param {string} userId - User identifier
 * @param {string|number} videoId - Video identifier
 * @returns {VideoProgress|null}
 */
export function resetVideoProgress(userId, videoId) {
  if (!userId || videoId == null || videoId === '') return null;
  const all = readAll(userId);
  const key = String(videoId);
  const reset = {
    viewCount: 0,
    secondsWatched: 0,
    lastWatchedAt: null,
    watched: false,
  };
  all[key] = reset;
  writeAll(userId, all);
  return reset;
}

/**
 * Clear all progress for a user (for debugging/testing).
 * @param {string} userId - User identifier
 * @returns {void}
 */
export function clearProgress(userId) {
  if (!userId) return;
  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch {
    // Ignore errors
  }
}

/**
 * Estimate current storage usage for a user (in characters, roughly bytes).
 * @param {string} userId - User identifier
 * @returns {number}
 */
export function getStorageSize(userId) {
  if (!userId) return 0;
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    return raw ? raw.length : 0;
  } catch {
    return 0;
  }
}
